/**
 * Conditions By Body System
 * Groups and displays conditions organized by body system
 */

import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import type { PreExistingCondition, ThingToNote } from '../../types/insurance';

interface ConditionsByBodySystemProps {
  conditions: PreExistingCondition[] | ThingToNote[];
  type: 'preExisting' | 'thingsToNote';
  onDiseaseClick?: (diseaseId: number, diseaseName: string) => void;
}

const ConditionsByBodySystem: React.FC<ConditionsByBodySystemProps> = ({ conditions, type, onDiseaseClick }) => {
  // Group by body system
  const grouped = conditions.reduce((acc, condition) => {
    const system = condition.bodySystem;
    if (!acc[system]) {
      acc[system] = [];
    }
    acc[system].push(condition);
    return acc;
  }, {} as Record<string, (PreExistingCondition | ThingToNote)[]>);

  const bodySystems = Object.keys(grouped).sort();

  // Auto-expand if 3 or fewer systems
  const defaultExpanded = bodySystems.length <= 3;

  return (
    <Box>
      {bodySystems.map((system) => (
        <Accordion key={system} defaultExpanded={defaultExpanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {system} ({grouped[system].length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} divider={<Divider />}>
              {grouped[system].map((condition, index) => (
                <Box key={index}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {'condition' in condition ? condition.condition : condition.finding}
                  </Typography>

                  {/* Linked GekkoVet disease + predisposing count chips */}
                  {(condition.diseaseId || ('relatedConditions' in condition && condition.relatedConditions?.length)) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                      {condition.diseaseId && condition.diseaseName && (
                        <Chip
                          label={condition.diseaseName}
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<LocalHospitalIcon />}
                          onClick={() => onDiseaseClick?.(condition.diseaseId!, condition.diseaseName!)}
                          clickable
                          sx={{ mb: 0.5 }}
                        />
                      )}
                      {type === 'preExisting' &&
                        'relatedConditions' in condition &&
                        condition.relatedConditions &&
                        condition.relatedConditions.length > 0 && (
                          <Chip
                            label={`${condition.relatedConditions.length} predisposing condition${condition.relatedConditions.length !== 1 ? 's' : ''}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        )}
                    </Stack>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {condition.veterinarianNotes}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip label={condition.dateOfVisit} size="small" variant="outlined" />
                    <Chip label={condition.hospital} size="small" variant="outlined" />
                  </Stack>

                  {/* Occurrences */}
                  {condition.occurrences && condition.occurrences.length > 1 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Occurred {condition.occurrences.length} times:
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                        {condition.occurrences.map((occ, i) => (
                          <Chip
                            key={i}
                            label={`${occ.date}${occ.hospital ? ` - ${occ.hospital}` : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Related conditions (pre-existing only) */}
                  {type === 'preExisting' &&
                    'relatedConditions' in condition &&
                    condition.relatedConditions &&
                    condition.relatedConditions.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                          Related Predisposing Conditions:
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {condition.relatedConditions.map((related, i) => (
                            <Box key={i}>
                              <Typography variant="body2" fontWeight="bold">
                                {related.condition}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {related.description}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ConditionsByBodySystem;
